import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { cn } from "@/lib/utils";
import { isStarterPlan } from "../config/planAccess";
import { RevenueChartCard } from "./revenue/RevenueChartCard";
import {
  SERVICES_CHART_CARD_ICON_COLOR,
  SERVICES_CONTENT_GAP,
} from "./services/constants";
import {
  DEFAULT_REVIEWS_SEGMENT,
  parseReviewsSegment,
} from "./reviews/reviewsSegment";
import {
  downloadReviewsDistributionExport,
  downloadReviewsExport,
  downloadReviewsPreCheckoutExport,
  downloadReviewsTrendExport,
} from "./reviews/reviewsTabExport";
import {
  createEmptyCategoryAverages,
  createEmptyCategoryBreakdowns,
  createEmptyStarDistribution,
  DEFAULT_CATEGORY_KEYS,
  buildEmptyPreCheckoutTrend,
  buildEmptyRatingTrend,
} from "./reviews/reviewsTabDefaults";
import { computeReviewsKpiDeltas } from "./reviews/reviewsKpiDeltas";
import {
  mapAverageByCategory,
  mapDistributionToSegments,
  mapOverallToSummary,
  mapOverTimeToRatingTrend,
} from "./reviews/reviewsTabMappers";
import { ReviewsAverageRatingCard } from "./reviews/ReviewsAverageRatingCard";
import { ReviewsCategoryBreakdownCard } from "./reviews/ReviewsCategoryBreakdownCard";
import { ReviewsCommunicationReviewsCard } from "./reviews/ReviewsCommunicationReviewsCard";
import { ReviewsDistributionCard } from "./reviews/ReviewsDistributionCard";
import { ReviewsKpiCards } from "./reviews/ReviewsKpiCards";
import { ReviewsPreCheckoutChart } from "./reviews/ReviewsPreCheckoutChart";
import { ReviewsRelatedTable } from "./reviews/ReviewsRelatedTable";
import { ReviewsRequestReviewsCard } from "./reviews/ReviewsRequestReviewsCard";
import { ReviewsSegmentPills } from "./reviews/ReviewsSegmentPills";
import { ReviewsTrendChart } from "./reviews/ReviewsTrendChart";
import type { CategoryKey, ReviewsSegment } from "./reviews/types";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useReviewsAnalytics } from "../hooks/useReviewsAnalytics";
import { useReviewsFeedbacks } from "../hooks/useReviewsFeedbacks";

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

const STARTER_REVIEW_SEGMENTS: readonly ReviewsSegment[] = [
  "reservations",
  "communication",
];

function getReviewsSegment(
  rawSegment: string | null,
  hasStarterPlan: boolean,
): ReviewsSegment {
  const parsedSegment = parseReviewsSegment(rawSegment);

  if (hasStarterPlan && parsedSegment === "requests") {
    return DEFAULT_REVIEWS_SEGMENT;
  }

  return parsedSegment;
}

function getVisibleReviewSegments(
  hasStarterPlan: boolean,
): readonly ReviewsSegment[] | undefined {
  return hasStarterPlan ? STARTER_REVIEW_SEGMENTS : undefined;
}

function useNormalizeStarterReviewsSegment(
  hasStarterPlan: boolean,
  searchParams: URLSearchParams,
  setSearchParams: SetSearchParams,
) {
  useEffect(() => {
    if (!hasStarterPlan || searchParams.get("reviewsSegment") !== "requests") {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set("tab", "reviews");
      nextParams.delete("reviewsSegment");
      return nextParams;
    });
  }, [hasStarterPlan, searchParams, setSearchParams]);
}

export const ReviewsTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);
  const { userProfile } = useAuthenticatedUser();
  const hasStarterPlan = isStarterPlan(userProfile);

  const segment = getReviewsSegment(
    searchParams.get("reviewsSegment"),
    hasStarterPlan,
  );
  const deferredSegment = useDeferredValue(segment);
  const reviewsAnalytics = useReviewsAnalytics(dateRange, deferredSegment);
  const reviewsFeedbacks = useReviewsFeedbacks(
    reviewsAnalytics.hotelId,
    reviewsAnalytics.from,
    reviewsAnalytics.to,
    deferredSegment,
    reviewsAnalytics.isReady,
  );
  const isContentPending =
    segment !== deferredSegment ||
    reviewsAnalytics.isLoading ||
    reviewsAnalytics.isFetching ||
    reviewsFeedbacks.isFetching ||
    reviewsAnalytics.overallPrevious.isFetching;
  const visibleReviewSegments = getVisibleReviewSegments(hasStarterPlan);
  useNormalizeStarterReviewsSegment(
    hasStarterPlan,
    searchParams,
    setSearchParams,
  );

  const emptyStarDistribution = useMemo(
    () => createEmptyStarDistribution(),
    [],
  );

  const viewModel = useMemo(() => {
    const { from, to } = reviewsAnalytics;
    const summary = mapOverallToSummary(reviewsAnalytics.overall.data);
    const starDistribution = mapDistributionToSegments(
      reviewsAnalytics.distribution.data,
      emptyStarDistribution,
    );
    const categoryData = mapAverageByCategory(
      reviewsAnalytics.averageByCategory.data,
    );
    const overTimeData = mapOverTimeToRatingTrend(
      reviewsAnalytics.overTime.data,
    );

    const categoryKeys: CategoryKey[] =
      categoryData.categoryKeys.length > 0
        ? categoryData.categoryKeys
        : DEFAULT_CATEGORY_KEYS;

    const categoryAverages = {
      ...createEmptyCategoryAverages(),
      ...categoryData.categoryAverages,
    };

    const categoryBreakdowns =
      deferredSegment === "reservations" && reviewsFeedbacks.data
        ? reviewsFeedbacks.data.categoryBreakdowns
        : createEmptyCategoryBreakdowns();

    const trendCategoryKeys = overTimeData.categoryKeys;
    const trendUsesCategories =
      overTimeData.categoriesSupported && trendCategoryKeys.length > 0;

    const ratingTrend =
      overTimeData.ratingTrend.length > 0
        ? overTimeData.ratingTrend
        : buildEmptyRatingTrend(from, to, {
            categoriesSupported: trendUsesCategories,
            categoryKeys,
          });

    const preCheckoutTrend =
      deferredSegment === "reservations" &&
      (reviewsFeedbacks.data?.preCheckoutTrend.length ?? 0) > 0
        ? reviewsFeedbacks.data!.preCheckoutTrend
        : buildEmptyPreCheckoutTrend(from, to);

    const kpiDeltas = computeReviewsKpiDeltas(
      summary,
      mapOverallToSummary(reviewsAnalytics.overallPrevious.data),
    );

    return {
      summary,
      kpiDeltas,
      starDistribution,
      categoryKeys,
      categoryAverages,
      categoryBreakdowns,
      ratingTrend,
      categoriesSupported: trendUsesCategories,
      trendCategoryKeys,
      preCheckoutTrend,
      relatedReviews: reviewsFeedbacks.data?.relatedReviews ?? [],
    };
  }, [
    deferredSegment,
    emptyStarDistribution,
    reviewsAnalytics,
    reviewsFeedbacks.data,
  ]);

  const handleSegmentChange = useCallback(
    (value: ReviewsSegment) => {
      setSearchParams((previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.set("tab", "reviews");

        if (value === DEFAULT_REVIEWS_SEGMENT) {
          nextParams.delete("reviewsSegment");
        } else {
          nextParams.set("reviewsSegment", value);
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const canExport =
    reviewsAnalytics.isReady &&
    !reviewsAnalytics.isLoading &&
    !reviewsAnalytics.isError &&
    !isExporting;
  const hasDistributionData = viewModel.starDistribution.some(
    (segment) => segment.value > 0,
  );
  const hasRatingTrendData = viewModel.ratingTrend.some((point) => {
    if (
      viewModel.categoriesSupported &&
      viewModel.trendCategoryKeys.length > 0
    ) {
      return viewModel.trendCategoryKeys.some(
        (key) => Number(point[key] ?? 0) > 0,
      );
    }

    return Number(point.overallRating ?? 0) > 0;
  });
  const showPreCheckoutWidget = segment === "reservations";
  const showRatingBreakdown = segment === "reservations";
  const showRequestReviewsBlock = segment === "requests";
  const showCommunicationReviewsBlock = segment === "communication";

  const handleExportReviews = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      setIsExporting(true);
      try {
        const params = {
          hotelId: reviewsAnalytics.hotelId,
          from: reviewsAnalytics.from,
          to: reviewsAnalytics.to,
          tab: reviewsAnalytics.tab,
          format,
        };

        await downloadReviewsExport({ ...params, dataset: "over-time" });
        await downloadReviewsExport({ ...params, dataset: "distribution" });

        toast.success(
          t(
            "analytics.reviews.toast.exportSuccess",
            "Reviews analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.reviews.toast.exportError",
            "Failed to export reviews analytics",
          ),
        );
      } finally {
        setIsExporting(false);
      }
    },
    [
      canExport,
      reviewsAnalytics.from,
      reviewsAnalytics.hotelId,
      reviewsAnalytics.tab,
      reviewsAnalytics.to,
      t,
    ],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport,
      onExport: handleExportReviews,
    }),
    [canExport, handleExportReviews],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  const handleDistributionExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!canExport || !hasDistributionData) {
        return;
      }

      downloadReviewsDistributionExport({
        hotelId: reviewsAnalytics.hotelId,
        from: reviewsAnalytics.from,
        to: reviewsAnalytics.to,
        tab: reviewsAnalytics.tab,
        format,
        segments: viewModel.starDistribution,
        totalReviews: viewModel.summary.reviewsCount,
      });
    },
    [
      canExport,
      hasDistributionData,
      reviewsAnalytics.from,
      reviewsAnalytics.hotelId,
      reviewsAnalytics.tab,
      reviewsAnalytics.to,
      viewModel.starDistribution,
      viewModel.summary.reviewsCount,
    ],
  );

  const handlePreCheckoutExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      downloadReviewsPreCheckoutExport({
        hotelId: reviewsAnalytics.hotelId,
        from: reviewsAnalytics.from,
        to: reviewsAnalytics.to,
        tab: reviewsAnalytics.tab,
        format,
        data: viewModel.preCheckoutTrend,
      });
    },
    [
      canExport,
      reviewsAnalytics.from,
      reviewsAnalytics.hotelId,
      reviewsAnalytics.tab,
      reviewsAnalytics.to,
      viewModel.preCheckoutTrend,
    ],
  );

  const handleTrendExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!canExport || !hasRatingTrendData) {
        return;
      }

      downloadReviewsTrendExport({
        hotelId: reviewsAnalytics.hotelId,
        from: reviewsAnalytics.from,
        to: reviewsAnalytics.to,
        tab: reviewsAnalytics.tab,
        format,
        data: viewModel.ratingTrend,
        categoriesSupported: viewModel.categoriesSupported,
        categoryKeys: viewModel.trendCategoryKeys,
      });
    },
    [
      canExport,
      hasRatingTrendData,
      reviewsAnalytics.from,
      reviewsAnalytics.hotelId,
      reviewsAnalytics.tab,
      reviewsAnalytics.to,
      viewModel.categoriesSupported,
      viewModel.ratingTrend,
      viewModel.trendCategoryKeys,
    ],
  );

  const statusCardProps = {
    title: t("analytics.reviews.detailsTitle", "Reviews details"),
    iconColor: SERVICES_CHART_CARD_ICON_COLOR,
    width: "full" as const,
    showExport: false,
  };

  let mainContent: React.ReactNode;

  if (!reviewsAnalytics.isReady) {
    mainContent = (
      <RevenueChartCard {...statusCardProps}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.reviews.missingFilters",
            "Select a hotel and date range to view reviews analytics.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else if (reviewsAnalytics.isError) {
    mainContent = (
      <RevenueChartCard {...statusCardProps}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.reviews.loadError",
            "Unable to load reviews analytics. Try again.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else {
    const preCheckoutExportDisabled =
      !canExport ||
      viewModel.preCheckoutTrend.every(
        (point) => point.preCheckoutReviews + point.postStayReviews === 0,
      );
    const distributionExportDisabled = !canExport || !hasDistributionData;
    const ratingExportDisabled = !canExport || !hasRatingTrendData;

    mainContent = (
      <>
        <ReviewsKpiCards
          summary={viewModel.summary}
          kpiDeltas={viewModel.kpiDeltas}
        />

        <div className="flex w-full max-w-full flex-col gap-4 xl:flex-row">
          <ReviewsDistributionCard
            segments={viewModel.starDistribution}
            totalReviews={viewModel.summary.reviewsCount}
            onExport={handleDistributionExport}
            exportDisabled={distributionExportDisabled}
          />
          {showPreCheckoutWidget ? (
            <ReviewsPreCheckoutChart
              data={viewModel.preCheckoutTrend}
              onExport={handlePreCheckoutExport}
              exportDisabled={preCheckoutExportDisabled}
            />
          ) : null}
        </div>

        {showRatingBreakdown && viewModel.categoryKeys.length > 0 ? (
          <div
            className="flex w-full max-w-full items-stretch"
            style={{ gap: SERVICES_CONTENT_GAP }}
          >
            <ReviewsAverageRatingCard
              overallRating={viewModel.summary.overallRating}
              categoryKeys={viewModel.categoryKeys}
              categoryAverages={viewModel.categoryAverages}
            />
            <ReviewsCategoryBreakdownCard
              category="purity"
              rows={viewModel.categoryBreakdowns.purity}
            />
            <ReviewsCategoryBreakdownCard
              category="comfort"
              rows={viewModel.categoryBreakdowns.comfort}
            />
            <ReviewsCategoryBreakdownCard
              category="service"
              rows={viewModel.categoryBreakdowns.service}
            />
          </div>
        ) : null}

        {showRequestReviewsBlock ? (
          <ReviewsRequestReviewsCard
            reviewsCount={viewModel.summary.reviewsCount}
          />
        ) : null}

        {showCommunicationReviewsBlock ? (
          <ReviewsCommunicationReviewsCard
            reviewsCount={viewModel.summary.reviewsCount}
          />
        ) : null}

        <ReviewsTrendChart
          data={viewModel.ratingTrend}
          categoriesSupported={viewModel.categoriesSupported}
          categoryKeys={viewModel.trendCategoryKeys}
          onExport={handleTrendExport}
          exportDisabled={ratingExportDisabled}
        />

        <ReviewsRelatedTable
          reviews={viewModel.relatedReviews}
          hotelId={reviewsAnalytics.hotelId}
          from={reviewsAnalytics.from}
          to={reviewsAnalytics.to}
          tab={reviewsAnalytics.tab}
        />
      </>
    );
  }

  return (
    <section className="space-y-6 font-sans">
      <ReviewsSegmentPills
        value={segment}
        onChange={handleSegmentChange}
        visibleSegments={visibleReviewSegments}
      />

      <div
        className={cn(
          "flex w-full max-w-full flex-col transition-opacity duration-150",
          isContentPending && "opacity-70",
        )}
        style={{ gap: SERVICES_CONTENT_GAP }}
      >
        {mainContent}
      </div>
    </section>
  );
};
